/* ToyMatch share card generator.

   Draws the top three matches onto a 1080x1920 canvas and gives it to the
   visitor as a download. Deliberately standalone: it reads what it needs back
   out of the rendered results grid rather than being wired into
   toymatch-quiz.js, so the card can be redesigned without touching the
   scoring logic or rewriting that file.

   Why a canvas and not a server: Shopify serves product images with
   access-control-allow-origin *, so they can be drawn and then exported.
   Without that header the canvas would be tainted and toBlob() would throw,
   which is what usually forces this kind of feature onto a backend.

   Note there is not a single regular expression in this file. Theme writes go
   through a JSON API and backslashes in regex literals are a reliable way to
   corrupt the upload, so string handling here is done longhand on purpose. */
(function () {
  'use strict';

  var W = 1080, H = 1920;

  /* Plain margins, not story safe areas. Instagram overlays its own chrome on
     roughly the top and bottom 250px of a story, and an earlier version held
     everything inside that band — which left the composition stranded in the
     middle. The call here is to fill the frame and let anyone posting it crop,
     so the URL at the foot may sit under the reply bar if the image is used
     uncropped. */
  var MARGIN_TOP = 90, MARGIN_BOTTOM = 80, PAD = 60;
  var CX = W / 2;
  var CONTENT_W = W - PAD * 2;

  /* Sampled from the live quiz in dark mode rather than guessed, so the card
     matches the screen the visitor has just come from.

     BADGE_BG is deliberately off-white, not #fff. A pure white pill vanishes
     into the many product shots that are photographed on white, which is
     exactly where the match percentage most needs to read. The hairline does
     the rest of that work. */
  var INK = '#ececec';
  var INK_DIM = 'rgba(236,236,236,0.62)';
  var TAG_BG = 'rgba(255,255,255,0.14)';
  var BADGE_BG = '#ebe8e1';
  var BADGE_INK = '#0b0b0c';
  var BADGE_EDGE = 'rgba(0,0,0,0.16)';
  // Now doubles as the header's divider rule colour, not just a panel edge —
  // the panel itself is gone (see drawHeader).
  var RULE_EDGE = 'rgba(255,255,255,0.32)';
  var FONT = 'obviously-variable, sans-serif';

  /* One weight for everything read as a single flat shout. This is the
     hierarchy: the lockup and the percentages carry weight, product names and
     the heading sit lighter, and the runner-up names and URL are lighter
     still. */
  var W_LOCKUP = '600';
  var W_HEADING = '500';
  var W_TITLE = '500';
  var W_TAG = '500';
  var W_BADGE = '600';
  var W_RUNNER = '400';
  var W_URL = '400';

  // Two lines, not three — paired with the full REGULATION wordmark on the
  // right of the header instead of the heart mark, so the store name only
  // needs to appear once on the card.
  var APP_NAME = ['ASS TOY', 'MATCH MAKER'];

  /* The store runs on more than one domain — regulation.co.uk and
     regulation.store are the same Shopify shop — so the URL printed on the
     card follows whatever host the quiz was actually taken on. Sending a
     .co.uk visitor's friends to the .store storefront would look careless,
     and the path is a shop-wide URL redirect so it resolves on either.

     Theme previews and the admin editor run on *.myshopify.com, which is a
     real host but not one to print on artwork people keep, so those fall
     through to the canonical link Shopify emits and then to a hardcoded
     default. Without that guard the first card anyone generated from a
     preview would carry an internal domain. */
  var URL_PATH = '/toymatch';
  var URL_FALLBACK = 'regulation.store';

  function tidyHost(host) {
    host = String(host || '').toLowerCase();
    if (host.indexOf('www.') === 0) host = host.substring(4);
    if (!host) return '';
    if (host.indexOf('.myshopify.com') > -1) return '';
    if (host === 'localhost' || host.indexOf('127.0.0.1') > -1) return '';
    return host;
  }

  function shareUrl() {
    var host = tidyHost(window.location.hostname);
    if (!host) {
      var link = document.querySelector('link[rel="canonical"]');
      if (link && link.href) {
        try { host = tidyHost(new URL(link.href).hostname); } catch (e) { host = ''; }
      }
    }
    return (host || URL_FALLBACK) + URL_PATH;
  }

  function loadImage(src) {
    return new Promise(function (resolve) {
      if (!src) return resolve(null);
      var img = new Image();
      // Required for export. The on-page <img> has no crossOrigin attribute,
      // so this may re-request the file into the CORS cache partition rather
      // than reusing the cached copy; the header allows it either way.
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { resolve(null); };
      img.src = src;
    });
  }

  function roundPath(ctx, x, y, w, h, r) {
    if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, w, h, r); return; }
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  // Canvas radial gradients are circular, but every gradient in the quiz is an
  // ellipse. Scaling the space before drawing is the standard way to get one.
  function ellipseWash(ctx, cx, cy, rx, ry, stops) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(1, ry / rx);
    var g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
    for (var i = 0; i < stops.length; i++) g.addColorStop(stops[i][0], stops[i][1]);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(0, 0, rx, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /* The on-screen quiz keeps its glows subtle because they sit behind live
     text for minutes at a time. A share card is looked at for two seconds in
     a feed, so the brand red is pushed considerably harder here — these are
     deliberately not the same alphas as toymatch-quiz-dark.css. */
  function drawBackground(ctx) {
    ctx.fillStyle = '#0b0b0c';
    ctx.fillRect(0, 0, W, H);
    ellipseWash(ctx, CX, 0, W * 1.2, H * 0.8,
      [[0, '#241f2b'], [0.44, '#14131a'], [1, '#08080a']]);
    ellipseWash(ctx, W * 0.26, H * 0.26, W * 0.52, H * 0.44,
      [[0, 'rgba(224,5,30,0.42)'], [0.68, 'rgba(224,5,30,0.05)'], [1, 'rgba(224,5,30,0)']]);
    ellipseWash(ctx, W * 0.82, H * 0.62, W * 0.5, H * 0.42,
      [[0, 'rgba(224,5,30,0.34)'], [0.68, 'rgba(224,5,30,0.04)'], [1, 'rgba(224,5,30,0)']]);
    ellipseWash(ctx, W * 0.12, H * 0.9, W * 0.44, H * 0.3,
      [[0, 'rgba(224,5,30,0.2)'], [0.7, 'rgba(224,5,30,0)'], [1, 'rgba(224,5,30,0)']]);
  }

  // object-fit: cover, clipped to a rounded box.
  function drawCover(ctx, img, x, y, w, h, r) {
    ctx.save();
    roundPath(ctx, x, y, w, h, r);
    ctx.fillStyle = '#f4f4f4';
    ctx.fill();
    ctx.clip();
    if (img) {
      var s = Math.max(w / img.naturalWidth, h / img.naturalHeight);
      var dw = img.naturalWidth * s, dh = img.naturalHeight * s;
      ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
    }
    ctx.restore();
  }

  // Canvas has no text wrapping, and product titles here run very long, so
  // this wraps to a line budget and ellipsises whatever is left over.
  function wrapLines(ctx, text, maxW, maxLines) {
    var raw = String(text || '').split(' ');
    var words = [];
    for (var i = 0; i < raw.length; i++) if (raw[i].length) words.push(raw[i]);
    var lines = [], cur = '', leftover = false;
    while (words.length) {
      var word = words.shift();
      var test = cur ? cur + ' ' + word : word;
      if (ctx.measureText(test).width <= maxW) { cur = test; continue; }
      if (!cur) { cur = word; } else { words.unshift(word); }
      lines.push(cur); cur = '';
      if (lines.length === maxLines) { leftover = words.length > 0; break; }
    }
    if (cur) { if (lines.length < maxLines) lines.push(cur); else leftover = true; }
    if (leftover && lines.length) {
      var last = lines[lines.length - 1];
      while (last.length > 1 && ctx.measureText(last + '…').width > maxW) {
        last = last.substring(0, last.length - 1);
      }
      while (last.length && (last.charAt(last.length - 1) === ' ' || last.charAt(last.length - 1) === ',')) {
        last = last.substring(0, last.length - 1);
      }
      lines[lines.length - 1] = last + '…';
    }
    return lines;
  }

  /* Product names here are long enough that a fixed size clipped most of them.
     This walks down a list of sizes and takes the first that fits the line
     budget without needing an ellipsis, so a short name stays large and a very
     long one shrinks rather than losing half its words. */
  function fitText(ctx, text, maxW, maxLines, sizes, weight) {
    var lines, i, j;
    for (i = 0; i < sizes.length; i++) {
      ctx.font = weight + ' ' + sizes[i] + 'px ' + FONT;
      lines = wrapLines(ctx, text, maxW, maxLines);
      var clipped = false;
      for (j = 0; j < lines.length; j++) {
        if (lines[j].charAt(lines[j].length - 1) === '…') clipped = true;
      }
      if (!clipped) return { size: sizes[i], lines: lines };
    }
    var last = sizes[sizes.length - 1];
    ctx.font = weight + ' ' + last + 'px ' + FONT;
    return { size: last, lines: wrapLines(ctx, text, maxW, maxLines) };
  }

  // Draws a fitted block of text centred inside a fixed-height box, so the
  // layout below it never moves regardless of how many lines a name needs.
  // Used for the runner-up names, which sit side by side and need their
  // reserved space to match regardless of which one has the longest title.
  function drawTextBlock(ctx, fit, cx, top, boxH, colour) {
    var lineH = Math.round(fit.size * 1.25);
    var start = top + (boxH - fit.lines.length * lineH) / 2;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = colour;
    for (var i = 0; i < fit.lines.length; i++) {
      ctx.fillText(fit.lines[i], cx, start + i * lineH);
    }
  }

  // Same fitted-text input as drawTextBlock, but anchored to the top instead
  // of centred in a fixed box. Used for the hero title: centring a short,
  // common one-line title inside a box sized for the worst-case three lines
  // left a wide band of empty space above it, which is what made the title
  // and the tags below it read as disconnected from the image above them.
  // Returns the height actually consumed so the caller can advance a running
  // y by exactly that, rather than by a fixed reservation.
  function drawTextTop(ctx, fit, cx, top, colour) {
    var lineH = Math.round(fit.size * 1.25);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = colour;
    for (var i = 0; i < fit.lines.length; i++) {
      ctx.fillText(fit.lines[i], cx, top + i * lineH);
    }
    return fit.lines.length * lineH;
  }

  function pillWidth(ctx, text, h, fontPx, weight) {
    ctx.font = weight + ' ' + fontPx + 'px ' + FONT;
    return ctx.measureText(text).width + Math.round(h * 0.42) * 2;
  }

  function pill(ctx, text, x, y, h, fontPx, weight, bg, fg, edge) {
    ctx.font = weight + ' ' + fontPx + 'px ' + FONT;
    var padX = Math.round(h * 0.42);
    var w = ctx.measureText(text).width + padX * 2;
    roundPath(ctx, x, y, w, h, h / 2);
    ctx.fillStyle = bg;
    ctx.fill();
    if (edge) {
      ctx.lineWidth = 2;
      ctx.strokeStyle = edge;
      ctx.stroke();
    }
    ctx.fillStyle = fg;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x + padX, y + h / 2 + 1);
    return w;
  }

  // Reads the rendered grid rather than the quiz's internal state, which is
  // what keeps this file decoupled from toymatch-quiz.js.
  function readResults(root) {
    var cards = root.querySelectorAll('.toymatch-quiz__card');
    var items = [];
    for (var i = 0; i < cards.length && items.length < 3; i++) {
      var c = cards[i];
      var img = c.querySelector('.toymatch-quiz__card-image');
      var title = c.querySelector('.toymatch-quiz__card-title');
      var badge = c.querySelector('.toymatch-quiz__match');
      var tagEls = c.querySelectorAll('.toymatch-quiz__card-tag');
      var tags = [];
      for (var t = 0; t < tagEls.length && t < 2; t++) tags.push(tagEls[t].textContent);
      items.push({
        src: img ? img.getAttribute('src') : '',
        title: title ? title.textContent : '',
        match: badge ? badge.textContent : '',
        tags: tags
      });
    }
    return items;
  }

  // Mirrors the possessive used for the results heading. Duplicated rather
  // than imported so this file stays independent; if the heading wording
  // changes, change it here too.
  function possessive(raw) {
    var name = String(raw || '');
    while (name.length && name.charAt(0) === ' ') name = name.substring(1);
    name = name.split(' ')[0] || '';
    name = name.substring(0, 20);
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.substring(1) + "'s";
  }

  /* App name set left, flush to the margin; the full REGULATION wordmark set
     right, flush to the opposite margin; a hairline rule underneath spanning
     the same width, closing the header off from the card body.

     No panel behind this any more — with the wordmark doing the branding job
     on its own at a size large enough to read cleanly, a translucent box
     behind two lines of text stopped earning its keep. The rule is what now
     separates the header from the content, the way it does on the on-page
     quiz's own progress bar.

     Wordmark width is fixed rather than derived like the app name, since it's
     a raster asset: sizing it here rather than in the footer (where it used
     to also appear) is deliberately the one place it's drawn at a size big
     enough to stay crisp — the same asset went soft at the ~230px the footer
     used to give it. */
  function drawHeader(ctx, wordmark, y) {
    var nameSize = 48, nameLineH = 54;
    var textBlockH = APP_NAME.length * nameLineH;

    var wmW = wordmark ? 340 : 0;
    var wmH = wordmark ? wmW * wordmark.naturalHeight / wordmark.naturalWidth : 0;

    var rowH = Math.max(textBlockH, wmH);

    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#ffffff';
    ctx.font = W_LOCKUP + ' ' + nameSize + 'px ' + FONT;
    var textTop = y + (rowH - textBlockH) / 2;
    for (var i = 0; i < APP_NAME.length; i++) {
      ctx.fillText(APP_NAME[i], PAD, textTop + i * nameLineH + 4);
    }

    if (wordmark) {
      ctx.drawImage(wordmark, W - PAD - wmW, y + (rowH - wmH) / 2, wmW, wmH);
    }

    var ruleY = y + rowH + 30;
    ctx.strokeStyle = RULE_EDGE;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(PAD, ruleY);
    ctx.lineTo(W - PAD, ruleY);
    ctx.stroke();

    return ruleY - y;
  }

  // Fixed height reserved for each runner-up's name, sized for the worst
  // case (2 wrapped lines), so runner card heights don't depend on which one
  // happens to have the longest name — they sit side by side, so a shifting
  // height there would misalign the row. The hero title doesn't need this:
  // it's the only thing at that width, drawn top-anchored via drawTextTop
  // instead, so a short, common one-line title sits right under the image
  // rather than centred in space reserved for a much longer one.
  var RUNNER_TITLE_BOX = 68;

  function render(bag, items, who) {
    var canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    var ctx = canvas.getContext('2d');
    drawBackground(ctx);

    var hero = items[0];
    var runners = items.slice(1);
    var y = MARGIN_TOP;

    // Trimming the panel out of the header freed a lot of vertical room —
    // rather than leave it all as one dead gap above the footer, some of it
    // is spent here and at the hero/runner boundary below so the extra space
    // reads as generous breathing room throughout instead of an afterthought
    // at the bottom.
    y += drawHeader(ctx, bag.wordmark, y) + 56;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#ffffff';
    ctx.font = W_HEADING + ' 52px ' + FONT;
    ctx.fillText(who ? who + ' top matches' : 'My top matches', CX, y);
    y += 62 + 34;

    var heroSize = 540;
    var hx = CX - heroSize / 2;
    drawCover(ctx, hero.img, hx, y, heroSize, heroSize, 24);
    if (hero.match) {
      var bw = pillWidth(ctx, hero.match, 56, 28, W_BADGE);
      pill(ctx, hero.match, hx + heroSize - bw - 20, y + 20, 56, 28, W_BADGE, BADGE_BG, BADGE_INK, BADGE_EDGE);
    }
    y += heroSize + 20;

    // Top-anchored, not the fixed centred box the runners use below — see
    // the note on drawTextTop. y advances by the lines this title actually
    // took, so a one-line title keeps the tags close instead of leaving a
    // gap sized for three.
    var heroTitleFit = fitText(ctx, hero.title, CONTENT_W, 3, [40, 36, 32, 28], W_TITLE);
    y += drawTextTop(ctx, heroTitleFit, CX, y, INK) + 18;

    if (hero.tags.length) {
      var gap = 14, total = 0, widths = [];
      for (var t = 0; t < hero.tags.length; t++) {
        widths[t] = pillWidth(ctx, hero.tags[t], 48, 25, W_TAG);
        total += widths[t];
      }
      total += gap * (hero.tags.length - 1);
      var tx = CX - total / 2;
      for (var t2 = 0; t2 < hero.tags.length; t2++) {
        pill(ctx, hero.tags[t2], tx, y, 48, 25, W_TAG, TAG_BG, INK, null);
        tx += widths[t2] + gap;
      }
    }
    y += 48 + 50;

    if (runners.length) {
      var rSize = 290, rGap = 56;
      var rowW = runners.length * rSize + (runners.length - 1) * rGap;
      var rx = CX - rowW / 2;
      for (var r = 0; r < runners.length; r++) {
        var it = runners[r];
        var ix = rx + r * (rSize + rGap);
        drawCover(ctx, it.img, ix, y, rSize, rSize, 18);
        if (it.match) {
          var rbw = pillWidth(ctx, it.match, 44, 23, W_BADGE);
          pill(ctx, it.match, ix + rSize - rbw - 12, y + 12, 44, 23, W_BADGE, BADGE_BG, BADGE_INK, BADGE_EDGE);
        }
        drawTextBlock(ctx, fitText(ctx, it.title, rSize, 2, [28, 25, 22], W_RUNNER),
          ix + rSize / 2, y + rSize + 14, RUNNER_TITLE_BOX, INK_DIM);
      }
      y += rSize + 14 + RUNNER_TITLE_BOX;
    }

    /* The footer is pinned to the bottom margin rather than flowing on from
       the runners. Sitting it directly under them left it looking like a
       caption on the last row instead of the foot of the card, and the gap
       moved about whenever a name needed an extra line. Math.max keeps it
       honest if the content above ever grows into it.

       Just the URL now — the wordmark that used to sit above it moved up into
       the header, where it's drawn larger and reads cleanly instead of
       softening at the small size the footer gave it. Printing it twice on
       one card never earned the repetition. */
    var urlH = 36;
    var footerTop = Math.max(y + 40, H - MARGIN_BOTTOM - urlH);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = INK_DIM;
    ctx.font = W_URL + ' 30px ' + FONT;
    ctx.fillText(shareUrl(), CX, footerTop);

    return canvas;
  }

  function download(canvas, filename) {
    canvas.toBlob(function (blob) {
      if (!blob) return;
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.parentNode.removeChild(a);
      window.setTimeout(function () { URL.revokeObjectURL(url); }, 5000);
    }, 'image/jpeg', 0.92);
  }

  function initShare(quiz) {
    var btn = quiz.querySelector('[data-quiz-share]');
    var resultsEl = quiz.querySelector('[data-quiz-results]');
    var grid = quiz.querySelector('[data-quiz-grid]');
    if (!btn || !resultsEl) return;

    var probe = document.createElement('canvas');
    if (!probe.toBlob || !probe.getContext) return;

    var cached = null;
    var cachedName = '';

    function sync() {
      var has = quiz.querySelectorAll('.toymatch-quiz__card').length > 0;
      var want = !(has && !resultsEl.hidden);
      // Guarded on purpose. setAttribute queues a mutation record even when
      // the value is unchanged, so an unguarded write here would retrigger
      // the observer that called it and spin the renderer forever.
      if (btn.hidden !== want) btn.hidden = want;
    }

    // The grid is watched for children (it is rebuilt on every retake) and the
    // results block for its own hidden attribute. Neither observation covers
    // the button, which sits in the results header — watching a subtree that
    // contained it would let this feed itself.
    var observer = new MutationObserver(function () { cached = null; sync(); });
    if (grid) observer.observe(grid, { childList: true });
    observer.observe(resultsEl, { attributes: true, attributeFilter: ['hidden'] });
    sync();

    btn.addEventListener('click', function () {
      if (cached) { download(cached, cachedName); return; }
      var items = readResults(quiz);
      if (!items.length) return;

      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Preparing…';

      var nameEl = quiz.querySelector('[name="firstName"]');
      var who = possessive(nameEl ? nameEl.value : '');
      var brandEl = quiz.querySelector('.toymatch-quiz__brand-img');

      // Just the wordmark now — the separate heart-logo fetch went away with
      // the header panel it used to sit on.
      var jobs = [loadImage(brandEl ? brandEl.getAttribute('src') : '')];
      for (var i = 0; i < items.length; i++) jobs.push(loadImage(items[i].src));

      // Text must not be drawn before the brand font resolves, or the card
      // renders in a fallback face the first time it is used.
      var fontReady = (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();

      Promise.all([fontReady, Promise.all(jobs)]).then(function (res) {
        var imgs = res[1];
        var bag = { wordmark: imgs[0] };
        for (var i = 0; i < items.length; i++) items[i].img = imgs[i + 1];
        var canvas = render(bag, items, who);
        var slug = who ? '-' + who.substring(0, who.length - 2).toLowerCase() : '';
        cachedName = 'regulation-toymatch' + slug + '.jpg';
        cached = canvas;
        download(canvas, cachedName);
      }).catch(function () {
        // A failed card must never look like a broken page.
      }).then(function () {
        btn.disabled = false;
        btn.textContent = label;
      });
    });
  }

  function boot() {
    var quizzes = document.querySelectorAll('.toymatch-quiz');
    for (var i = 0; i < quizzes.length; i++) initShare(quizzes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
